export const JOY_HOTEL_DISCOUNT_CODE_PARAM = 'joyHotelDiscountCode';

/**
 * Room occupancy input type that accepts both strict number arrays
 * and GraphQL InputMaybe arrays (which may contain null/undefined).
 */
interface RoomOccupancyInput
  extends Readonly<{
    adults: number;
    childrenAges?: ReadonlyArray<number | null | undefined> | null;
  }> {}

/**
 * Formats a room's occupancy into the URL parameter format.
 * @example
 * // returns "a2:c5:c7" for a room with 2 adults and 2 children aged 5 and 7
 * formatRoomOccupancyToString({ adults: 2, childrenAges: [5, 7] })
 */
export const formatRoomOccupancyToString = (room: RoomOccupancyInput): string => {
  const adultsParam = `a${room.adults}`;
  const validChildrenAges = room.childrenAges?.filter((age): age is number => age != null);
  const childrenParam = validChildrenAges?.length ? `:c${validChildrenAges.join(':c')}` : '';
  return `${adultsParam}${childrenParam}`;
};

/**
 * Formats multiple room occupancies into a comma-separated URL parameter string.
 * @example
 * // returns "a2:c5:c7,a3" for 2 rooms: first with 2 adults and 2 children, second with 3 adults
 * formatRoomOccupanciesToString([{ adults: 2, childrenAges: [5, 7] }, { adults: 3 }])
 */
export const formatRoomOccupanciesToString = (rooms: ReadonlyArray<RoomOccupancyInput>): string => {
  return rooms.map(formatRoomOccupancyToString).join(',');
};

interface ConvertToJoyNativeCheckoutUrlParams
  extends Readonly<{
    url: string;
    currency: string;
    checkIn?: string;
    checkOut?: string;
    roomsString?: string;
    discountCode?: string;
  }> {}

export const convertToJoyNativeCheckoutUrl = ({ url, checkIn, checkOut, roomsString, discountCode, currency }: ConvertToJoyNativeCheckoutUrlParams): URL => {
  const redirectUrl = new URL(url);

  const urlParams: Readonly<Record<string, string | undefined>> = {
    checkIn,
    checkOut,
    rooms: roomsString,
  };

  ['checkIn', 'checkOut', 'rooms'].forEach(param => {
    const value = urlParams[param];
    if (value) {
      redirectUrl.searchParams.set(param, value);
    }
  });

  if (discountCode) {
    redirectUrl.searchParams.set(JOY_HOTEL_DISCOUNT_CODE_PARAM, discountCode);
  }
  redirectUrl.searchParams.set('currency', currency);

  return redirectUrl;
};
